import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { useLanguage } from '../i18n/language';
import { dayjsLocales } from '../i18n/muiLocales';
import {
  ColorModeContext,
  nextColorMode,
  readStoredColorMode,
  storeColorMode,
  type ColorMode,
} from './colorMode';
import { buildTheme } from './theme';

export function ColorModeProvider({ children }: { children: ReactNode }) {
  // Read here rather than passed in: the theme needs it, and this provider is what owns the theme.
  const { language } = useLanguage();
  const [mode, setModeState] = useState<ColorMode>(readStoredColorMode);
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');

  const resolvedMode = mode === 'system' ? (prefersDark ? 'dark' : 'light') : mode;

  const setMode = useCallback((next: ColorMode) => {
    setModeState(next);
    storeColorMode(next);
  }, []);

  const value = useMemo(
    () => ({
      mode,
      resolvedMode,
      setMode,
      cycleMode: () => setMode(nextColorMode[mode]),
    }),
    [mode, resolvedMode, setMode],
  );

  // Rebuilt on a language change as well, which is what carries MUI's own translations through.
  const theme = useMemo(() => buildTheme(resolvedMode, language), [resolvedMode, language]);

  return (
    <ColorModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        {/* CssBaseline is what makes the page background follow the mode. */}
        <CssBaseline />
        {/* Sits here rather than in App so the pickers' formats follow the same language the
            theme's own translations do. */}
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dayjsLocales[language]}>
          {children}
        </LocalizationProvider>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}
